import type { AxiosRequestConfig } from 'axios';
import {
  CreateFRIAResponse,
  UpdateFRIAPayload,
  FRIAFinishResult,
} from '../types/FRIA.types';
import { AIGenerateJobResponse } from '../types/AIJob.types';
import API from './ApiBase';
import PythonAIAPI from './PythonAIApiBase';

type ApiRequestConfig = AxiosRequestConfig & { skipErrorToast?: boolean };

/**
 * Get FRIA draft by AI System ID
 */
export const getFRIAByAiSystem = async (
  aiSystemId: string,
  options?: { suppressErrorToast?: boolean }
): Promise<CreateFRIAResponse> => {
  const config: ApiRequestConfig = {};

  if (options?.suppressErrorToast) {
    config.skipErrorToast = true;
  }

  const response = await API.get<CreateFRIAResponse>(
    `/fria/ai-systems/${aiSystemId}`,
    config
  );
  return response.data;
};

/**
 * Get FRIA draft by ID
 */
export const getFRIAById = async (id: string) => {
  const response = await API.get<CreateFRIAResponse>(`/fria/${id}`);
  return response.data;
};

/**
 * Get FRIA by Document ID
 */
export const getFRIAByDocumentId = async (documentId: string) => {
  const response = await API.get<CreateFRIAResponse>(
    `/fria/documents/${documentId}`
  );
  return response.data;
};

/**
 * Create FRIA draft for an AI System
 */
export const createFRIADraft = async (aiSystemId: string) => {
  const response = await API.post<CreateFRIAResponse>(
    `/fria/ai-systems/${aiSystemId}`
  );
  return response.data;
};

/**
 * Update existing FRIA (draft only)
 */
export const updateFRIA = async (
  friaId: string,
  payload: UpdateFRIAPayload
) => {
  const response = await API.put<CreateFRIAResponse>(
    `/fria/${friaId}`,
    payload
  );
  return response.data;
};

/**
 * Complete FRIA
 */
export const completeFRIA = async (
  friaId: string,
  payload: UpdateFRIAPayload
) => {
  const response = await API.put<FRIAFinishResult>(
    `/fria/${friaId}/complete`,
    payload
  );
  return response.data;
};

// AI Suggestion types
export interface FRIAFillResult {
  friaId: string;
  updatedFields: string[];
  aiSystemId: string;
  contentJson?: CreateFRIAResponse;
}

export interface FRIAFillResponse {
  success: boolean;
  data: FRIAFillResult;
}

/**
 * Suggest FRIA content using AI
 */
export const suggestFRIAContent = async (
  friaId: string
): Promise<FRIAFillResponse> => {
  const response = await PythonAIAPI.patch<FRIAFillResponse>(`/fria/${friaId}`);
  return response.data;
};

export const generateFRIAContent = async (
  friaId: string,
  forceRefresh: boolean = false,
  options?: { suppressErrorToast?: boolean }
): Promise<AIGenerateJobResponse> => {
  const config: ApiRequestConfig = {};

  if (options?.suppressErrorToast) {
    config.skipErrorToast = true;
  }

  const response = await API.post<AIGenerateJobResponse>(
    `/fria/${friaId}/generate`,
    { forceRefresh },
    config
  );
  return response.data;
};

